import React, { useCallback, useMemo, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AddGroup, Status, StatusRows, Line, StatusRow1, SortableGroupWrapper, GroupItems } from './styles';
import DeleteModal from '../../modals/deleteModal';
import { TaskGroupInterface } from '../../../interfaces/TaskInterface';
import { Autotext, Box, IconHidden, LabelTitle, LeftRow, Row, Subtitle, Title } from '../styles';
import Button from '../../button';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE, MENU_OPTIONS } from '../../../global/constants';
import Modal from '../../modals/modal';
import { Dropdown, DropdownItem } from '../../dropdown';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../../mobx/rootStore';
import IconButton from '../../button/iconButton';
import SwitchInput from '../../input/switchInput';

interface SortableGroupItemProps {
  group: TaskGroupInterface;
  onClickMenu: (key: string | number, group: TaskGroupInterface) => void;
  menuItems: { key: number; label: string }[];
}

const SortableGroupItem: React.FC<SortableGroupItemProps> = ({ group, onClickMenu, menuItems }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group?.id });
  const cssTransform = CSS.Translate.toString(transform ? { ...transform } : null) || undefined;

  return (
    <SortableGroupWrapper
      ref={setNodeRef}
      isDragging={isDragging}
      cssTransform={cssTransform}
      cssTransition={transition || undefined}>
      <Line>
        <IconHidden
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...attributes}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...listeners}>
          <SVGIcon
            name='new-three-dots-vertical-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </IconHidden>
        <StatusRows isDragging={isDragging}>
          <LabelTitle>{group?.name}</LabelTitle>
          <Dropdown
            modalTitle='Group'
            activeClassName='active-state'
            content={
              <IconButton
                iconName='three-dots-horizontal-icon'
                size={ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className='more-icon'
              />
            }
            trigger='click'>
            {menuItems.map((item) => (
              <DropdownItem
                key={item.key}
                label={item.label}
                onClick={() => onClickMenu(item.key, group)}
                tone={item.label === 'Delete' ? 'critical' : 'primary'}
              />
            ))}
          </Dropdown>
        </StatusRows>
      </Line>
    </SortableGroupWrapper>
  );
};

interface Props {
  themeMode: { theme: string };
  handleDragEnd: (sourceIndex: number, destinationIndex: number) => void;
  taskGroups: TaskGroupInterface[];
  onClickEditGroup: (group: TaskGroupInterface) => void;
  deleteGroup: (group: TaskGroupInterface) => void;
  loading: boolean;
  setModalOpen: (value: boolean) => void;
  onUpdateGroup: (value: boolean) => void;
  loadingButton: boolean;
  isTemplateProject?: boolean;
}

const GroupSettingCard: React.FC<Props> = (props) => {
  const {
    handleDragEnd,
    taskGroups,
    onClickEditGroup,
    deleteGroup,
    loading,
    setModalOpen,
    onUpdateGroup,
    loadingButton,
    isTemplateProject
  } = props;
  //States
  const [deleteModelOpen, setDeleteModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TaskGroupInterface>();
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  //Mobx store variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { currentProject: projectDetails } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const currentProject = isTemplateProject ? currentProjectTemplate : projectDetails;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const sortableIds = useMemo(() => taskGroups.map((group) => group.id), [taskGroups]);

  const menuItems = [
    {
      key: MENU_OPTIONS.EDIT,
      label: 'Edit'
    },
    {
      key: MENU_OPTIONS.DELETE,
      label: 'Delete'
    }
  ];

  const openDeleteModel = useCallback((group: TaskGroupInterface) => {
    setSelectedGroup(group);
    setDeleteModalOpen(true);
  }, []);

  const onClose = () => {
    setDeleteModalOpen(false);
  };

  const onDeleteGroup = useCallback(() => {
    if (selectedGroup) deleteGroup(selectedGroup);
    setDeleteModalOpen(false);
  }, [deleteGroup, selectedGroup]);

  const onClickAddGroup = useCallback(() => {
    setModalOpen(true);
  }, [setModalOpen]);

  const handleClickMenu = useCallback(
    (key: string | number, group: TaskGroupInterface) => {
      key = Number(key);
      if (key === MENU_OPTIONS.EDIT) {
        onClickEditGroup(group);
      } else if (key === MENU_OPTIONS.DELETE) {
        openDeleteModel(group);
      }
    },
    [onClickEditGroup, openDeleteModel]
  );

  const activeGroup = useMemo(
    () => taskGroups.find((group) => group.id === activeGroupId) || null,
    [activeGroupId, taskGroups]
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    setActiveGroupId(event.active.id as string);
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveGroupId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const sourceIndex = taskGroups.findIndex((group) => group.id === active.id);
      const destinationIndex = taskGroups.findIndex((group) => group.id === over.id);
      if (sourceIndex === -1 || destinationIndex === -1) return;

      handleDragEnd(sourceIndex, destinationIndex);
    },
    [taskGroups, handleDragEnd]
  );

  return (
    <>
      <Box>
        <Row>
          <LeftRow>
            <Title>Groups</Title>
            <Subtitle>Groups define task progression and can be customized and reordered</Subtitle>
          </LeftRow>
          <Autotext>
            {loadingButton ? (
              <Button variant={BUTTON_TYPE.PRIMARY} isLoading={loadingButton} />
            ) : (
              <SwitchInput
                checked={currentProject?.isGroupEnabled}
                onChange={() => onUpdateGroup(!currentProject?.isGroupEnabled)}
              />
            )}
          </Autotext>
        </Row>
        {currentProject?.isGroupEnabled && (
          <Status>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={onDragStart}
              onDragCancel={() => setActiveGroupId(null)}
              onDragEnd={onDragEnd}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <GroupItems>
                  {taskGroups?.map((group) => {
                    return (
                      <SortableGroupItem
                        key={group?.id}
                        group={group}
                        onClickMenu={handleClickMenu}
                        menuItems={menuItems}
                      />
                    );
                  })}
                </GroupItems>
              </SortableContext>
              <DragOverlay>
                {activeGroup ? (
                  <Line>
                    <IconHidden isDragging={true}>
                      <SVGIcon
                        name='new-three-dots-vertical-icon'
                        width={ICON_SIZE.MD}
                        height={ICON_SIZE.MD}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--text-secondary)'
                      />
                    </IconHidden>
                    <StatusRows isDragging={true}>
                      <LabelTitle>{activeGroup?.name}</LabelTitle>
                      <IconButton
                        iconName='three-dots-horizontal-icon'
                        size={ICON_BUTTON_SIZE.SMALL}
                        variant={ICON_BUTTON_TYPE.TERTIARY}
                        className='more-icon'
                      />
                    </StatusRows>
                  </Line>
                ) : null}
              </DragOverlay>
            </DndContext>
            <StatusRow1 onClick={onClickAddGroup}>
              <AddGroup>Add Group</AddGroup>
            </StatusRow1>
          </Status>
        )}
      </Box>
      <Modal open={deleteModelOpen} onClose={onClose} maxWidth={'334px'}>
        <DeleteModal
          onClose={onClose}
          onOk={() => onDeleteGroup()}
          loading={loading}
          modalTitle='Delete Group?'
          description='Are you sure you want to delete this group?'
        />
      </Modal>
    </>
  );
};

export default observer(GroupSettingCard);
